// src/components/RelatedProducts/RelatedProducts.jsx
import { useState, useEffect } from 'react';
import { Carousel, Container } from 'react-bootstrap';
import '../../../assets/css/customer/productPage/RelatedProducts.css';
import ProductCard from '../../../components/customer/cards/productCard/ProductCard';
import { RelatedProductsSkeleton } from '../../../components/general/skeleton/SkeletonBlock';

// ✅ LOCAL IMAGES ONLY
import wheat          from '../../../assets/images/products/wheat.webp';
import millet         from '../../../assets/images/products/millet.jpeg';
import cottonseeds    from '../../../assets/images/products/cottonseeds.jpg';
import blueberries    from '../../../assets/images/products/Blueberries.jpeg';
import redLentils     from '../../../assets/images/products/redLentiles.jpg';
import hybridSugarcane from '../../../assets/images/products/hybridSugarcane.jpg';
import fertilizerNPK  from '../../../assets/images/products/fertilizerNPK.webp';
import chickpeas      from '../../../assets/images/products/checkpieas.jpg';

const RelatedProducts = () => {
  const [loading, setLoading]           = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);

  // ✅ ASYNC DATA FETCH — 300ms skeleton
  useEffect(() => {
    const fetchRelated = async () => {
      try {
        // TODO: Replace with real API → await fetch('/api/related-products')
        await new Promise((resolve) => setTimeout(resolve, 300));

        // ✅ 8 COMPLETE products with local images + units
        setRelatedProducts([
          {
            id: 1,
            name: 'Wheat Seeds Akbar-19',
            category: 'Seeds',
            subcategory: 'Seeds',
            price: 2800,
            oldPrice: null,
            unit: '40kg bag',
            rating: 5,
            reviews: 89,
            image: wheat,
            shopName: 'Punjab Seed Corporation — Lahore',
            shopId: 1,
            badge: { type: 'new', text: 'New' },
          },
          {
            id: 2,
            name: 'Premium Millet Seeds',
            category: 'Wheat & Grains',
            subcategory: 'Wheat & Grains',
            price: 900,
            oldPrice: 1200,
            unit: '10kg bag',
            rating: 5,
            reviews: 45,
            image: millet,
            shopName: 'Punjab Grain Market — Lahore',
            shopId: 2,
            badge: { type: 'hot', text: 'Hot' },
          },
          {
            id: 3,
            name: 'BT Cotton Seeds',
            category: 'Seeds',
            subcategory: 'Seeds',
            price: 1800,
            oldPrice: null,
            unit: '5kg bag',
            rating: 4,
            reviews: 44,
            image: cottonseeds,
            shopName: 'Green Valley Seeds Store',
            shopId: 3,
            badge: { type: 'organic', text: 'Organic' },
          },
          {
            id: 4,
            name: 'Fresh Blueberries',
            category: 'Fruits',
            subcategory: 'Fruits',
            price: 650,
            oldPrice: 800,
            unit: 'per kg',
            rating: 5,
            reviews: 67,
            image: blueberries,
            shopName: 'Green Valley Organic Farm',
            shopId: 4,
            badge: { type: 'new', text: 'New' },
          },
          {
            id: 5,
            name: 'Red Lentils (Masoor Dal)',
            category: 'Pulses',
            subcategory: 'Pulses',
            price: 280,
            oldPrice: null,
            unit: 'per kg',
            rating: 4,
            reviews: 47,
            image: redLentils,
            shopName: 'Karachi Dal Center — Karachi',
            shopId: 5,
            badge: null,
          },
          {
            id: 6,
            name: 'Hybrid Sugarcane Cutting',
            category: 'Sugarcane',
            subcategory: 'Sugarcane',
            price: 450,
            oldPrice: null,
            unit: '40kg bundle',
            rating: 4,
            reviews: 48,
            image: hybridSugarcane,
            shopName: 'RYK Sugar Mills — Rahim Yar Khan',
            shopId: 6,
            badge: null,
          },
          {
            id: 7,
            name: 'NPK Organic Fertilizer',
            category: 'Fertilizers',
            subcategory: 'Fertilizers',
            price: 1800,
            oldPrice: 2200,
            unit: '25kg bag',
            rating: 4,
            reviews: 88,
            image: fertilizerNPK,
            shopName: 'National Fertilizer Corp — Faisalabad',
            shopId: 7,
            badge: { type: 'sale', text: 'Sale' },
          },
          {
            id: 8,
            name: 'Desi Chickpeas (Chana)',
            category: 'Pulses',
            subcategory: 'Pulses',
            price: 220,
            oldPrice: 260,
            unit: 'per kg',
            rating: 5,
            reviews: 59,
            image: chickpeas,
            shopName: 'Chakwal Pulses Farm — Chakwal',
            shopId: 8,
            badge: { type: 'sale', text: 'Sale' },
          },
        ]);
      } catch (error) {
        console.error('Failed to load related products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRelated();
  }, []);

  const groupProducts = (size) => {
    const groups = [];
    for (let i = 0; i < relatedProducts.length; i += size)
      groups.push(relatedProducts.slice(i, i + size));
    return groups;
  };

  // ✅ LOADING STATE — single check only
  if (loading) return <RelatedProductsSkeleton />;

  const desktopGroups = groupProducts(4);
  const tabletGroups  = groupProducts(3);
  const mobileGroups  = groupProducts(2);

  return (
    <section className="related-products-section">
      <Container>
        <div className="section-header-related">
          <h2 className="section-title-related">Related Products</h2>
        </div>

        {/* Desktop — 4 per slide */}
        <div className="related-slider d-none d-lg-block">
          <Carousel indicators={false} controls={true} interval={3000} pause="hover" wrap={true}>
            {desktopGroups.map((group, i) => (
              <Carousel.Item key={i}>
                <div className="products-slide-row">
                  {group.map((product, index) => (
                    <div key={product.id} className="product-slide-col">
                      <ProductCard product={product} index={index} />
                    </div>
                  ))}
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Tablet — 3 per slide */}
        <div className="related-slider d-none d-md-block d-lg-none">
          <Carousel indicators={false} controls={false} interval={3000} pause="hover" wrap={true}>
            {tabletGroups.map((group, i) => (
              <Carousel.Item key={i}>
                <div className="products-slide-row">
                  {group.map((product, index) => (
                    <div key={product.id} className="product-slide-col">
                      <ProductCard product={product} index={index} />
                    </div>
                  ))}
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Mobile — 2 per slide */}
        <div className="related-slider d-block d-md-none">
          <Carousel indicators={false} controls={false} interval={3000} pause="hover" wrap={true}>
            {mobileGroups.map((group, i) => (
              <Carousel.Item key={i}>
                <div className="products-slide-row">
                  {group.map((product, index) => (
                    <div key={product.id} className="product-slide-col">
                      <ProductCard product={product} index={index} />
                    </div>
                  ))}
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        </div>
      </Container>
    </section>
  );
};

export default RelatedProducts;
